/* The ten materials of a lesson, collapsed to ten rows.

   Zen, but nothing lost. A lesson carries ten finished materials and every one
   of them is long; laid out end to end the page is a wall, and a wall is what a
   teacher scrolls past rather than reads. So each material is one row — what it
   is, one sentence, and the marks that answer "is this for my class" — and it
   opens where it stands.

   Two decisions that follow from how it is used:

     more than one row opens at a time. A teacher compares the exercise with
     the check that goes with it; an accordion that closes the first when the
     second opens makes that impossible.

     the open row is named in the address. `/m/<lekce>#hands-on` opens on the
     hands-on exercise, which is how one is handed to a colleague. The address
     is rewritten in place, never pushed: a back button that walks back through
     nine folds is a back button nobody uses twice.

   On paper everything is open — print.css and the fold rules in catalog.css see
   to that. A sheet that hides half its content is not a sheet.

   A row is in one of three states, and the reader can tell which without
   opening it. The material is here; or it is not made yet and the prompt below
   it is what makes it; or the lesson has decided it will not have this one and
   says why. The second is the state fifty-four converted lessons are in from
   end to end (item 12 of PLAN.md), so it had better read as an honest offer
   rather than as a gap — which is what it is: a goal and ten prompts is worth
   having. What it must never do is read as the first. The `hotové` mark is
   drawn from `ready` and from nothing else.

   OWNED BY ITEM 3 OF PLAN.md, third state added by ITEM 12. */

import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { T } from "../../blocks/shared";
import { ModelPrompt } from "../../blocks/prompts";
import { useI18n } from "../../lib/i18n";
import { GOAL_LEVELS, isAbsent } from "../../lib/types";
import type { GoalLevelId, KitItem, KitItemPresent, Localized } from "../../lib/types";
import { Ready } from "./Ready";

export function Kit({ kit, lesson }: { kit: KitItem[]; lesson: string }) {
  const { ui } = useI18n();
  const { hash } = useLocation();
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set());

  const wanted = decodeURIComponent(hash.replace(/^#/, ""));

  /* An address that names a material opens it and goes to it. The effect
     depends on the hash rather than running once, so a link followed while
     already on the page works the same as one followed from outside. */
  useEffect(() => {
    if (!wanted || !kit.some((item) => item.kind === wanted)) return;
    setOpen((prev) => (prev.has(wanted) ? prev : new Set(prev).add(wanted)));
    /* One frame later: the row has to be open before there is anything to
       scroll to. */
    const at = requestAnimationFrame(() => {
      document.getElementById(wanted)?.scrollIntoView({ block: "start" });
    });
    return () => cancelAnimationFrame(at);
  }, [wanted, kit]);

  /* replaceState, not the router: the address is a bookmark of what is open,
     not a step in the reader's history. It is read back from the live address
     rather than from the router, which never sees a replaceState and would
     answer with whatever the hash was when the page was entered. */
  function mark(kind: string | null) {
    window.history.replaceState(null, "", window.location.pathname
      + window.location.search + (kind ? `#${kind}` : ""));
  }

  function toggle(kind: string, next: boolean) {
    setOpen((prev) => {
      const now = new Set(prev);
      if (next) now.add(kind); else now.delete(kind);
      return now;
    });
    if (next) mark(kind);
    /* Closing the row the address names clears it; closing any other row
       leaves the address where it was. */
    else if (window.location.hash.slice(1) === kind) mark(null);
  }

  function all(state: boolean) {
    setOpen(state ? new Set(kit.filter((i) => !isAbsent(i)).map((i) => i.kind)) : new Set());
    /* Nine rows open at once, or none: either way the address names no one
       of them. */
    mark(null);
  }

  const openable = kit.filter((item) => !isAbsent(item)).length;
  const allOpen = openable > 0 && kit.every((item) => isAbsent(item) || open.has(item.kind));

  return (
    <section className="lesson-kit">
      <div className="cat-folds-bar">
        <h2 className="cat-h">{ui("lesson.kit")}</h2>
        <button type="button" className="cat-flat" onClick={() => all(!allOpen)}>
          {allOpen ? ui("material.collapseAll") : ui("material.expandAll")}
        </button>
      </div>
      <p className="lesson-kit-lead">{ui("lesson.kitLead")}</p>

      {kit.map((item) => (isAbsent(item)
        ? <AbsentRow key={item.kind} kind={item.kind} why={item.absent} />
        : <KitRow key={item.kind} item={item} lesson={lesson}
                  open={open.has(item.kind)} onToggle={toggle} />
      ))}
    </section>
  );
}

function KitRow({ item, lesson, open, onToggle }: {
  item: KitItemPresent; lesson: string;
  open: boolean; onToggle: (kind: string, next: boolean) => void;
}) {
  const { t, ui } = useI18n();
  return (
    /* The row is opened by a click, not by the browser: `onToggle` fires for a
       programmatic change too, so "expand all" would arrive here nine times and
       leave the address naming whichever row happened to be last. The click is
       the user, and the keyboard reaches it the same way. */
    <details className={"fold kit-row" + (item.ready ? "" : " is-unmade")}
             id={item.kind} open={open}>
      <summary className="fold-head kit-head"
               onClick={(e) => { e.preventDefault(); onToggle(item.kind, !open); }}>
        <span className="kit-kind">{ui(`kit.${item.kind}`)}</span>
        <span className="kit-said">
          <span className="fold-title">{t(item.title)}</span>
          <span className="kit-claim">{t(item.claim)}</span>
        </span>
        <Marks item={item} lesson={lesson} />
        <span className="fold-mark" aria-hidden="true" />
      </summary>
      <div className="fold-body">
        {item.ready ? <Ready ready={item.ready} print={item.print} /> : <NotMade />}
        <ModelPrompt prompt={item.prompt} />
        {item.note?.length ? (
          <div className="note">
            <h4>{ui("block.notes")}</h4>
            <ul>{item.note.map((n, i) => <li key={i}><T value={n} /></li>)}</ul>
          </div>
        ) : null}
      </div>
    </details>
  );
}

/* Not made yet. Said in the row rather than left to be inferred from an empty
   space, and said above the prompt, because the prompt is the answer to it. */
function NotMade() {
  const { ui } = useI18n();
  return (
    <p className="kit-unmade-said">
      <strong>{ui("lesson.notMade")}</strong> {ui("lesson.notMadeLead")}
    </p>
  );
}

/* A material that is not here says why, and keeps its row. Ten rows always: a
   reader has to be able to see what a lesson does not have without counting. */
function AbsentRow({ kind, why }: { kind: string; why: Localized }) {
  const { ui } = useI18n();
  return (
    <div className="fold kit-row is-absent">
      <div className="fold-head kit-head">
        <span className="kit-kind">{ui(`kit.${kind}`)}</span>
        <span className="kit-said">
          <span className="fold-title">{ui("lesson.absent")}</span>
          <span className="kit-claim"><T value={why} /></span>
        </span>
      </div>
    </div>
  );
}

/* The paper mark, and where it leads. A material that is finished and carried
   as blocks has a sheet of its own at `/tisk/<lekce>/<kind>`, and the mark is
   the way to it; one that is not made yet keeps the mark and stays plain,
   because there is nothing behind it yet. A link that leads to a blank page is
   worse than no link.

   `<a>` inside `<summary>`, so the click must not also open the row: the
   handler stops it before Kit's toggle sees it. */
function PrintTag({ item, lesson }: { item: KitItemPresent; lesson: string }) {
  const { ui, href } = useI18n();
  const label = ui("lesson.print");
  if (item.ready?.as !== "blocks") {
    return <span className="kit-tag is-print">{label}</span>;
  }
  // Through the router and through href(), like every other internal link: a
  // bare <a> with a hand-built locale prefix reloaded the whole application
  // and was a second place where "/en" was spelled out.
  return (
    <Link className="kit-tag is-print is-link"
          to={href(`/tisk/${lesson}/${item.kind}`)}
          onClick={(e) => e.stopPropagation()}
          title={ui("lesson.printSheet")}>{label}</Link>
  );
}

/* What a row says while it is shut. Minutes, which levels of the goal it
   serves, and then the three marks — hotové · prompt · tisk — that answer
   what the reader gets by opening it. `hotové` is the one that has to be
   right: it comes from `ready` and from nothing else, so a row can never look
   finished when it is not. */
function Marks({ item, lesson }: { item: KitItemPresent; lesson: string }) {
  const { ui } = useI18n();
  const served = GOAL_LEVELS.filter((l) => item.levels.includes(l));
  const said = `${ui("lesson.levels")}: ${served.map((l) => ui(`level.${l}`)).join(", ")}`;
  return (
    <span className="kit-marks">
      {item.minutes ? <span className="kit-mins">{item.minutes} {ui("lesson.minutes")}</span> : null}
      {/* One image, one label: read once by a screen reader, and a tooltip for
          whoever has a mouse and is looking at three dots. */}
      <span className="kit-levels" role="img" title={said} aria-label={said}>
        {GOAL_LEVELS.map((level: GoalLevelId) => (
          <i key={level} aria-hidden="true"
             className={"kit-dot" + (item.levels.includes(level) ? " on" : "")} />
        ))}
      </span>
      <span className="kit-tags">
        {item.ready ? <span className="kit-tag is-ready">{ui("lesson.markReady")}</span> : null}
        <span className="kit-tag">{ui("lesson.markPrompt")}</span>
        {item.print ? <PrintTag item={item} lesson={lesson} /> : null}
      </span>
    </span>
  );
}

